import { Injectable } from "@angular/core";
import { Cents, Instrument, Performance, Pitch, PitchedNote, UnpitchedNote } from "../model/music";
import { PitchedSound, Sound, stepsToMidiNoteNumbers, UnpitchedSound } from "../model/sound";
import { Assets } from "../model/assets";
import { WebAudioService } from "./webaudio.service";

/**
 * Plays sounds with a given instrument and pitch.
 */
@Injectable()
export class SoundService {

	//while loading sounds, the total number of sounds to load
	loadingSoundsTotalCount: number
	//while loading sounds,  the current number of already loaded sounds
	loadingSoundsLoadedCount: number


	constructor(private webAudio: WebAudioService) {
	}

	/**
	 * Plays the given performance, as soon as all required sounds are loaded.
	 */
	play(performance: Performance) {
		//ensure all required sounds are loaded
		this.loadSounds(performance).then(() => {
			this.playInternal(performance)
		}).catch(err => {
			console.error("Could not load sounds. Details:")
			console.error(err)
		})
	}

	/**
	 * Stops all currently playing sounds.
	 */
	stopAll() {
		this.webAudio.stopAll()
	}

	/**
	 * Plays the given performance. All required sounds must have been loaded already.
	 */
	//TODO: sound duration currently ignored
	private playInternal(performance: Performance) {
		for (let melody of performance.melodies) {
			let volume = melody.loudnessFactor
			for (let timedChord of melody.timedChords) {
				for (let note of timedChord.chord.notes) {
					let sound: Sound
					let correction = 0
					if (note instanceof PitchedNote) {
						sound = this.getSoundForPitch(note.pitch, melody.instrument)
						correction = (sound as PitchedSound).correction + note.tuning
					}
					else if (note instanceof UnpitchedNote) {
						sound = new UnpitchedSound(melody.instrument, note.sound)
					}
					let soundFileName = this.getSoundFileName(sound)
					this.webAudio.play(soundFileName, timedChord.startTime, volume, correction)
				}
			}
		}
	}

	/**
	 * Loads all sounds required for the given performance and caches
	 * them for later reuse. The progress is reported in the fields
	 * loadingSoundsTotalCount and loadingSoundsLoadedCount.
	 */
	loadSounds(performance: Performance): Promise<void> {
		let soundFileNames = this.collectSoundFileNames(performance)
		this.loadingSoundsTotalCount = soundFileNames.length
		this.loadingSoundsLoadedCount = 0
		return this.loadSoundsInternal(soundFileNames)
	}

	/**
	 * Recursively called to load all given sounds asynchronously.
	 */
	private loadSoundsInternal(remainingSoundFileNames: string[]): Promise<void> {
		return new Promise<void>((resolve, reject) => {
			let soundFileName = remainingSoundFileNames.splice(remainingSoundFileNames.length - 1, 1)[0]
			this.webAudio.loadSoundFile(soundFileName).then((audioBuffer) => {
				this.loadingSoundsLoadedCount++
				//more sounds to load?
				if (remainingSoundFileNames.length == 0) //finished
					resolve()
				else
					this.loadSoundsInternal(remainingSoundFileNames).then(() => resolve()).catch(err => reject(err))
			}).catch(err => reject(err))
		})
	}

	/**
	 * Gets the file names of all sounds required for the given performance.
	 */
	private collectSoundFileNames(performance: Performance): string[] {
		let sounds: Set<string> = new Set()
		for (let melody of performance.melodies) {
			for (let timedChord of melody.timedChords) {
				for (let note of timedChord.chord.notes) {
					let sound: Sound
					if (note instanceof PitchedNote)
						sound = this.getSoundForPitch(note.pitch, melody.instrument)
					else if (note instanceof UnpitchedNote)
						sound = new UnpitchedSound(melody.instrument, note.sound)
					sounds.add(this.getSoundFileName(sound))
				}
			}
		}
		return Array.from(sounds.keys())
	}

	/**
	 * Filename of the base pitch of the given sound. E.g. "Piano/A4"
	 */
	private getSoundFileName(sound: Sound): string {
		if (sound instanceof PitchedSound)
			return sound.instrument + "/" + sound.basePitch.step +
					(sound.basePitch.alter != 0 ? (sound.basePitch.alter == 1 ? "s" : "b") : "") +
					sound.basePitch.octave
		else if (sound instanceof UnpitchedSound)
			return sound.instrument + "/" + sound.soundName
		else
			throw new Error()
	}

	private getSoundForPitch(pitch: Pitch, instrument: Instrument): PitchedSound {
		let basePitch = this.getBestBasePitch(pitch, Assets.basePitches[instrument])
		let halfNotes = pitch.toMidiNoteNumber() - basePitch.toMidiNoteNumber()
		let correction = (halfNotes * 100) as Cents
		return new PitchedSound(pitch, instrument, basePitch, correction)
	}

	private getBestBasePitch(pitch: Pitch, basePitches: Pitch[]): Pitch {
		let note = pitch.toMidiNoteNumber()
		//at or below first base pitch?
		if (note <= basePitches[0].toMidiNoteNumber())
			return basePitches[0]
		//at or above last base pitch?
		if (note >= basePitches[basePitches.length - 1].toMidiNoteNumber())
			return basePitches[basePitches.length - 1]
		//find nearest pitch
		for (let i = 0; i < basePitches.length; i++) {
			let upperNote = basePitches[i].toMidiNoteNumber()
			if (note == upperNote) { //perfect match
				return basePitches[i]
			}
			else if (note < upperNote) { //inbetween
				let lowerNote = basePitches[i - 1].toMidiNoteNumber()
				if (note - lowerNote <= upperNote - note) //when nearer to lower note or equal, use lower note
					return basePitches[i - 1]
				else
					return basePitches[i]
			}
		}
	}

}


// WEBPACK FOOTER //
// ./src/service/sound.service.ts